"use client";

import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Box,
  Text,
  Button,
  HStack,
  VStack,
  Flex,
  IconButton,
  useToast,
  Spinner,
  Tooltip,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
} from "@chakra-ui/react";
import { ExternalLinkIcon } from "@chakra-ui/icons";
import { Trash2, Plus } from "lucide-react";
import { ChevronDownIcon } from "@chakra-ui/icons";

import {
  agentService,
  type RetellLLM,
} from "../../../services/agentService.ts";
import {
  knowledgeBaseService,
  type KnowledgeBase,
} from "../../../services/knowledgeBaseService.ts";
import { useAppTheme } from "../../../ThemeContext.tsx";

interface AgentKnowledgeBaseProps {
  agentId: string;
  llmId: string;
  initialLLM: RetellLLM | null;
  onLLMUpdate: (updatedLLM: RetellLLM) => void;
}

// ----------------------------------------------------
// 1) Create a re-usable CustomSelectWithIcons component
// ----------------------------------------------------

interface Option {
  value: string; // unique identifier
  label: string; // text to display
  icon: React.ReactElement; // icon to display
}

interface CustomSelectWithIconsProps {
  label?: string;
  options: Option[];
  onSelect: (value: string) => void;
  isDisabled?: boolean;
  isDark: boolean;
  buttonText?: string;
  buttonLeftIcon?: React.ReactElement;
  menuWidthMatchButton?: boolean; // whether to match the MenuList width to the button
}

const CustomSelectWithIcons: React.FC<CustomSelectWithIconsProps> = ({
  label,
  options,
  onSelect,
  isDisabled,
  isDark,
  buttonText,
  buttonLeftIcon,
  menuWidthMatchButton = true,
}) => {
  const buttonRef = useRef<HTMLButtonElement>(null);

  // Derive theme-based styles
  const cardBg = isDark ? "gray.900" : "white";
  const cardComponentsBg = isDark ? "gray.800" : "gray.100";
  const textColor = isDark ? "white" : "gray.800";
  const borderColor = isDark ? "gray.600" : "gray.300";

  return (
    <Box>
      {label && (
        <Text mb={2} fontWeight="medium" color={textColor}>
          {label}
        </Text>
      )}
      <Menu>
        <MenuButton
          as={Button}
          ref={buttonRef}
          leftIcon={buttonLeftIcon}
          rightIcon={<ChevronDownIcon />}
          size="xs"
          variant="outline"
          isDisabled={isDisabled}
          bg="transparent"
          color={textColor}
          borderColor={borderColor}
          _hover={{ bg: cardBg }}
          _expanded={{ bg: cardBg }}
          width={"100%"}
        >
          {buttonText || "Select"}
        </MenuButton>
        <MenuList
          bg={isDark ? "gray.800" : "gray.100"}
          borderColor={borderColor}
          minW={
            menuWidthMatchButton && buttonRef.current
              ? `${buttonRef.current.offsetWidth}px`
              : "100%"
          }
        >
          {options.map((option) => (
            <MenuItem
              key={option.value}
              onClick={() => onSelect(option.value)}
              icon={option.icon}
              _hover={{ bg: isDark ? "gray.700" : "gray.200" }}
              bg={cardComponentsBg}
              color={textColor}
              fontSize="sm"
            >
              {option.label}
            </MenuItem>
          ))}
        </MenuList>
      </Menu>
    </Box>
  );
};

// ----------------------------------------------------
// 2) Refactor your AgentKnowledgeBase component
// ----------------------------------------------------

export const AgentKnowledgeBase: React.FC<AgentKnowledgeBaseProps> = ({
  agentId,
  llmId,
  initialLLM,
  onLLMUpdate,
}) => {
  const [knowledgeBases, setKnowledgeBases] = useState<KnowledgeBase[]>([]);
  const [selectedKnowledgeBases, setSelectedKnowledgeBases] = useState<
    KnowledgeBase[]
  >([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isUpdating, setIsUpdating] = useState(false);
  const toast = useToast();
  const { resolvedTheme } = useAppTheme();
  const isDark = resolvedTheme === "dark";

  // Load all knowledge bases and the ones connected to this agent
  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);
      try {
        // Get all knowledge bases
        const allKnowledgeBases = await knowledgeBaseService.list();
        setKnowledgeBases(allKnowledgeBases);

        // Get the knowledge bases connected to this agent
        if (
          initialLLM?.knowledge_base_ids &&
          initialLLM.knowledge_base_ids.length > 0
        ) {
          const connectedKnowledgeBases = allKnowledgeBases.filter((kb) =>
            initialLLM.knowledge_base_ids?.includes(kb.knowledge_base_id)
          );
          setSelectedKnowledgeBases(connectedKnowledgeBases);
        }
      } catch (error) {
        console.error("Error loading knowledge bases:", error);
        toast({
          title: "Error loading knowledge bases",
          status: "error",
          duration: 3000,
        });
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [initialLLM, toast]);

  // Update the LLM with the selected knowledge bases
  const updateKnowledgeBases = useCallback(
    async (kbIds: string[] | null) => {
      if (!llmId) return;

      setIsUpdating(true);
      try {
        const updatedLLM = await agentService.updateRetellLLM(llmId, {
          agent_id: agentId,
          knowledge_base_ids: kbIds,
        });

        onLLMUpdate(updatedLLM);

        toast({
          title: "Knowledge bases updated",
          status: "success",
          duration: 2000,
        });

        return updatedLLM;
      } catch (error: any) {
        console.error("Error updating knowledge bases:", error);
        toast({
          title: "Error updating knowledge bases",
          description:
            error.response?.data?.message || "An unexpected error occurred",
          status: "error",
          duration: 3000,
        });
        return null;
      } finally {
        setIsUpdating(false);
      }
    },
    [llmId, agentId, onLLMUpdate, toast]
  );

  // Add a knowledge base to the agent
  const handleAddKnowledgeBase = useCallback(
    async (knowledgeBaseId: string) => {
      if (knowledgeBaseId === "add_new_kb") {
        // If the user selected "Add New Knowledge Base", open the KB page in a new tab:
        window.open("/knowledge-base", "_blank");
        return;
      }

      // Find the knowledge base in the list
      const knowledgeBase = knowledgeBases.find(
        (kb) => kb.knowledge_base_id === knowledgeBaseId
      );
      if (!knowledgeBase) return;

      // Check if it's already selected
      if (
        selectedKnowledgeBases.some(
          (kb) => kb.knowledge_base_id === knowledgeBaseId
        )
      ) {
        toast({
          title: "Knowledge base already added",
          status: "info",
          duration: 2000,
        });
        return;
      }

      // Add to selected knowledge bases
      const newSelectedKnowledgeBases = [
        ...selectedKnowledgeBases,
        knowledgeBase,
      ];
      setSelectedKnowledgeBases(newSelectedKnowledgeBases);

      // Update the LLM
      const kbIds = newSelectedKnowledgeBases.map((kb) => kb.knowledge_base_id);
      await updateKnowledgeBases(kbIds);
    },
    [knowledgeBases, selectedKnowledgeBases, updateKnowledgeBases, toast]
  );

  // Remove a knowledge base from the agent
  const handleRemoveKnowledgeBase = useCallback(
    async (knowledgeBaseId: string) => {
      // Remove from selected knowledge bases
      const newSelectedKnowledgeBases = selectedKnowledgeBases.filter(
        (kb) => kb.knowledge_base_id !== knowledgeBaseId
      );
      setSelectedKnowledgeBases(newSelectedKnowledgeBases);

      // Update the LLM
      const kbIds =
        newSelectedKnowledgeBases.length > 0
          ? newSelectedKnowledgeBases.map((kb) => kb.knowledge_base_id)
          : null;
      await updateKnowledgeBases(kbIds);
    },
    [selectedKnowledgeBases, updateKnowledgeBases]
  );

  // Prepare dropdown options
  // We transform the list of available KBs into Option[] for the CustomSelectWithIcons.
  const availableKnowledgeBases = knowledgeBases.filter(
    (kb) =>
      !selectedKnowledgeBases.some(
        (skb) => skb.knowledge_base_id === kb.knowledge_base_id
      )
  );

  const kbOptions = availableKnowledgeBases.map((kb) => ({
    value: kb.knowledge_base_id,
    label: kb.knowledge_base_name,
    icon: (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="16"
        height="16"
        viewBox="0 0 20 20"
        fill="none"
      >
        <path
          d="M3.25 14.875V4.75C3.25 4.15326 3.48705 3.58097 3.90901 3.15901C4.33097 2.73705 4.90326 2.5 5.5 2.5H16C16.1989 2.5 16.3897 2.57902 16.5303 2.71967C16.671 2.86032 16.75 3.05109 16.75 3.25V16.75C16.75 16.9489 16.671 17.1397 16.5303 17.2803C16.3897 17.421 16.1989 17.5 16 17.5H5.875C5.17881 17.5 4.51113 17.2234 4.01884 16.7312C3.52656 16.2389 3.25 15.5712 3.25 14.875ZM15.25 16V13.75H5.875C5.57663 13.75 5.29048 13.8685 5.0795 14.0795C4.86853 14.2905 4.75 14.5766 4.75 14.875C4.75 15.1734 4.86853 15.4595 5.0795 15.6705C5.29048 15.8815 5.57663 16 5.875 16H15.25ZM4.75 12.5028C5.10152 12.3358 5.48586 12.2495 5.875 12.25H15.25V4H5.5C5.30109 4 5.11032 4.07902 4.96967 4.21967C4.82902 4.36032 4.75 4.55109 4.75 4.75V12.5028Z"
          fill="currentColor"
        ></path>
      </svg>
    ),
  }));

  // Add a "special" option to create a new KB:
  const specialOption = {
    value: "add_new_kb",
    label: "Add New Knowledge Base",
    icon: <ExternalLinkIcon />,
  };

  // Final list of dropdown options (KBs + special entry)
  const dropdownOptions = [...kbOptions, specialOption];

  return (
    <Accordion allowToggle>
      <AccordionItem border="none">
        <h2>
          <AccordionButton
            py={3}
            px={4}
            _hover={{ bg: isDark ? "gray.800" : "gray.200" }}
          >
            <Box
              flex="1"
              textAlign="left"
              fontSize="sm"
              display="flex"
              alignItems="center"
              gap={2}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
              >
                <path
                  d="M3.25 14.875V4.75C3.25 4.15326 3.48705 3.58097 3.90901 3.15901C4.33097 2.73705 4.90326 2.5 5.5 2.5H16C16.1989 2.5 16.3897 2.57902 16.5303 2.71967C16.671 2.86032 16.75 3.05109 16.75 3.25V16.75C16.75 16.9489 16.671 17.1397 16.5303 17.2803C16.3897 17.421 16.1989 17.5 16 17.5H5.875C5.17881 17.5 4.51113 17.2234 4.01884 16.7312C3.52656 16.2389 3.25 15.5712 3.25 14.875ZM15.25 16V13.75H5.875C5.57663 13.75 5.29048 13.8685 5.0795 14.0795C4.86853 14.2905 4.75 14.5766 4.75 14.875C4.75 15.1734 4.86853 15.4595 5.0795 15.6705C5.29048 15.8815 5.57663 16 5.875 16H15.25ZM4.75 12.5028C5.10152 12.3358 5.48586 12.2495 5.875 12.25H15.25V4H5.5C5.30109 4 5.11032 4.07902 4.96967 4.21967C4.82902 4.36032 4.75 4.55109 4.75 4.75V12.5028Z"
                  fill="currentColor"
                ></path>
              </svg>
              Knowledge Base
            </Box>
            <AccordionIcon />
          </AccordionButton>
        </h2>
        <AccordionPanel pb={4} bg={isDark ? "gray.800" : "gray.100"}>
          {isLoading ? (
            <Box textAlign="center" py={4}>
              <Spinner size="sm" />
            </Box>
          ) : (
            <VStack spacing={4} align="stretch">
              <Text fontSize="sm" color={isDark ? "gray.400" : "gray.600"}>
                Add knowledge base to provide context to the agent.
              </Text>

              {/* List of selected knowledge bases */}
              {selectedKnowledgeBases.length > 0 && (
                <VStack spacing={2} align="stretch">
                  {selectedKnowledgeBases.map((kb) => (
                    <Flex
                      key={kb.knowledge_base_id}
                      justify="space-between"
                      align="center"
                      p={1.5}
                      bg={isDark ? "gray.700" : "gray.50"}
                      borderRadius="md"
                      minHeight="32px"
                    >
                      <HStack>
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="20"
                          height="20"
                          viewBox="0 0 20 20"
                          fill="none"
                        >
                          <path
                            d="M3.25 14.875V4.75C3.25 4.15326 3.48705 3.58097 3.90901 3.15901C4.33097 2.73705 4.90326 2.5 5.5 2.5H16C16.1989 2.5 16.3897 2.57902 16.5303 2.71967C16.671 2.86032 16.75 3.05109 16.75 3.25V16.75C16.75 16.9489 16.671 17.1397 16.5303 17.2803C16.3897 17.421 16.1989 17.5 16 17.5H5.875C5.17881 17.5 4.51113 17.2234 4.01884 16.7312C3.52656 16.2389 3.25 15.5712 3.25 14.875ZM15.25 16V13.75H5.875C5.57663 13.75 5.29048 13.8685 5.0795 14.0795C4.86853 14.2905 4.75 14.5766 4.75 14.875C4.75 15.1734 4.86853 15.4595 5.0795 15.6705C5.29048 15.8815 5.57663 16 5.875 16H15.25ZM4.75 12.5028C5.10152 12.3358 5.48586 12.2495 5.875 12.25H15.25V4H5.5C5.30109 4 5.11032 4.07902 4.96967 4.21967C4.82902 4.36032 4.75 4.55109 4.75 4.75V12.5028Z"
                            fill="currentColor"
                          ></path>
                        </svg>
                        <Text fontSize="sm">{kb.knowledge_base_name}</Text>
                      </HStack>
                      <Tooltip label="Remove">
                        <IconButton
                          aria-label="Remove"
                          icon={<Trash2 size={12} />}
                          size="xs"
                          variant="ghost"
                          isDisabled={isUpdating}
                          onClick={() =>
                            handleRemoveKnowledgeBase(kb.knowledge_base_id)
                          }
                          color={isDark ? "gray.300" : "gray.700"}
                          _hover={{ bg: isDark ? "gray.700" : "gray.200" }}
                        />
                      </Tooltip>
                    </Flex>
                  ))}
                </VStack>
              )}

              {/* Add button with our new CustomSelectWithIcons */}
              <CustomSelectWithIcons
                label=""
                options={dropdownOptions}
                onSelect={handleAddKnowledgeBase}
                isDisabled={isUpdating}
                isDark={isDark}
                buttonText="Add"
                buttonLeftIcon={<Plus size={14} />}
                menuWidthMatchButton={true}
              />
            </VStack>
          )}
        </AccordionPanel>
      </AccordionItem>
    </Accordion>
  );
};
